import { Button } from '@app/components/various/Button';
import { ChevronLeft, ChevronRight, DoubleChevronLeft, DoubleChevronRight } from '@app/components/various/Icons';
import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useDatePickerNavigationContext, useDatePickerSettingsContext } from '../context';

interface Props {
  className?: string;
}

export const DatePickerControls = ({ className }: Props) => {
  const { t } = useTranslation(['dates']);
  const prevButtonRef = React.useRef<HTMLButtonElement>(null);
  const { isDateView, isYearView, setView } = useDatePickerSettingsContext();
  const { calendarDates, onNextClick, onPrevClick, onSuperNextClick, onSuperPrevClick, visibleDate } = useDatePickerNavigationContext();

  const setMonthView = React.useCallback(() => setView('month'), [setView]);
  const setYearView = React.useCallback(() => setView('year'), [setView]);

  const yearViewLabel = React.useMemo(() => {
    const [firstDate] = calendarDates;
    const lastDate = calendarDates[calendarDates.length - 1];

    if (!isDefined(firstDate) || !isDefined(lastDate)) {
      return '';
    }

    return `${firstDate.year()} - ${lastDate.year()}`;
  }, [calendarDates]);

  return (
    <div className={cn('flex items-center justify-between border-b border-b-neutral-90 pb-2 pt-2', className)}>
      <div className="flex">
        <Button
          ref={prevButtonRef}
          icon={DoubleChevronLeft}
          name={isYearView ? t('dates:previous_decade') : t('dates:previous_year')}
          size="medium"
          color="secondary"
          variant="ghost"
          onClick={onSuperPrevClick}
        />
        {isDateView && (
          <Button
            size="medium"
            icon={ChevronLeft}
            name={t('dates:previous_month')}
            color="secondary"
            variant="ghost"
            onClick={onPrevClick}
          />
        )}
      </div>
      <div className="flex">
        {isYearView ?
          <span>{yearViewLabel}</span>
        : <Button color="secondary" variant="text" type="button" onClick={setYearView} className="!h-8 !px-1 !body-2">
            {visibleDate.format('YYYY')}
          </Button>
        }
        {isDateView && (
          <Button color="secondary" variant="text" type="button" onClick={setMonthView} className="!h-8 !min-w-8 !px-1 !body-2">
            {visibleDate.format('MM')}
          </Button>
        )}
      </div>
      <div className="flex">
        {isDateView && (
          <Button icon={ChevronRight} name={t('dates:next_month')} size="medium" color="secondary" variant="ghost" onClick={onNextClick} />
        )}
        <Button
          icon={DoubleChevronRight}
          name={isYearView ? t('dates:next_decade') : t('dates:next_year')}
          size="medium"
          color="secondary"
          variant="ghost"
          onClick={onSuperNextClick}
        />
      </div>
    </div>
  );
};
